import React, { useMemo, useState, useRef } from 'react';
import { CheckCircle2Icon, AlertCircleIcon, PhoneIcon, Loader2Icon } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL } from './shared';
// FORM 1 — QUICK CLAIM REVIEW · progressive 3-step form
// Kept as a focused child of FinalCTA. Hidden attribution fields, honeypot, validation states.
type ClaimCategory = 'work' | 'road' | 'public' | 'serious' | 'unsure';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const categories: {
  value: ClaimCategory;
  label: string;
}[] = [{
  value: 'work',
  label: 'Work injury'
}, {
  value: 'road',
  label: 'Road accident'
}, {
  value: 'public',
  label: 'Public / private place'
}, {
  value: 'serious',
  label: 'Serious injury'
}, {
  value: 'unsure',
  label: 'Not sure'
}];
const inputClass = 'w-full min-h-[48px] rounded-input border border-ink/15 bg-canvas px-4 text-body text-ink placeholder:text-slate/40 focus:border-forest focus:outline-none focus:ring-2 focus:ring-signal/40';
export function QuickClaimForm() {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState<ClaimCategory | null>(null);
  const [when, setWhen] = useState('');
  const [where, setWhere] = useState('');
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [contactTime, setContactTime] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const honeypot = useRef('');
  const hiddenFields = useMemo(() => ({
    landing_page: typeof window !== 'undefined' ? window.location.pathname : '',
    original_referrer: typeof document !== 'undefined' ? document.referrer : '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    gclid: '',
    fbclid: '',
    device_type: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
    timestamp: new Date().toISOString()
  }), []);
  function validateStep3() {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Please enter your name.';
    if (!phone.trim()) e.phone = 'Please enter a phone number.';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Please enter a valid email.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }
  async function handleSubmit(evt: React.FormEvent) {
    evt.preventDefault();
    if (honeypot.current) return; // bot caught
    if (!validateStep3()) return;
    setStatus('submitting');
    try {
      // Placeholder submission — wire to CRM/intake endpoint on integration.
      await new Promise(res => setTimeout(res, 1100));
      // eslint-disable-next-line no-console
      console.log('Claim review submission', {
        claim_category: category,
        when,
        where,
        description,
        name,
        phone,
        email,
        contactTime,
        ...hiddenFields
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }
  if (status === 'success') {
    return <div className="rounded-card border border-ink/5 bg-paper p-8 text-center shadow-card" data-id="element-253">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-forest/10 text-forest" data-id="element-254">
          <CheckCircle2Icon aria-hidden="true" className="h-6 w-6" strokeWidth={1.75} data-id="element-255" />
        </span>
        <h3 className="mt-4 font-serif text-h3 text-forest" data-id="element-256">
          Thank you — we have your details
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-body text-slate" data-id="element-257">
          A member of our friendly intake team will be in touch to arrange a
          confidential conversation about what happened. Prefer to talk now?
        </p>
        <a href={PHONE_TEL} className="mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-btn bg-signal px-6 text-body font-semibold text-paper hover:bg-signal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" data-id="element-258">
          <PhoneIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-259" />
          Prefer to talk now? Call {PHONE_DISPLAY}
        </a>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-card border border-ink/5 bg-paper p-6 shadow-card-lg md:p-8" aria-label="Quick claim review" data-id="element-260">
      {/* Progress */}
      <div className="mb-6 flex items-center gap-2" aria-hidden="true" data-id="element-261">
        {[1, 2, 3].map(n => <span key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? 'bg-signal' : 'bg-ink/10'}`} data-id="element-262" />)}
      </div>
      <p className="mb-5 text-small font-semibold uppercase tracking-wider text-slate/55" data-id="element-263">
        Step {step} of 3
      </p>

      {/* Honeypot */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" onChange={e => honeypot.current = e.target.value} data-id="element-264" />

      {step === 1 && <fieldset data-id="element-265">
          <legend className="text-h4 font-semibold text-ink" data-id="element-266">
            What happened?
          </legend>
          <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-id="element-267">
            {categories.map(c => <button key={c.value} type="button" onClick={() => {
          setCategory(c.value);
          setStep(2);
        }} className={`min-h-[48px] rounded-btn border px-4 text-left text-body transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal ${category === c.value ? 'border-forest bg-forest/[0.06] text-forest' : 'border-ink/15 text-ink hover:border-forest/50'}`} data-id="element-268">
                {c.label}
              </button>)}
          </div>
        </fieldset>}

      {step === 2 && <fieldset className="space-y-4" data-id="element-269">
          <legend className="text-h4 font-semibold text-ink" data-id="element-270">
            A few details
          </legend>
          <div data-id="element-271">
            <label htmlFor="qc-when" className="mb-1.5 block text-small font-medium text-ink" data-id="element-272">
              Approximately when did it happen?
            </label>
            <input id="qc-when" type="text" placeholder="e.g. March 2026" value={when} onChange={e => setWhen(e.target.value)} className={inputClass} data-id="element-273" />
          </div>
          <div data-id="element-274">
            <label htmlFor="qc-where" className="mb-1.5 block text-small font-medium text-ink" data-id="element-275">
              Where did it happen?
            </label>
            <input id="qc-where" type="text" placeholder="e.g. Perth CBD, a worksite" value={where} onChange={e => setWhere(e.target.value)} className={inputClass} data-id="element-276" />
          </div>
          <div data-id="element-277">
            <label htmlFor="qc-desc" className="mb-1.5 block text-small font-medium text-ink" data-id="element-278">
              Short description{' '}
              <span className="text-slate/45" data-id="element-279">(optional)</span>
            </label>
            <textarea id="qc-desc" rows={3} placeholder="In your own words — no legal terms needed" value={description} onChange={e => setDescription(e.target.value)} className={`${inputClass} py-3`} data-id="element-280" />
          </div>
          <div className="flex gap-3 pt-1" data-id="element-281">
            <button type="button" onClick={() => setStep(1)} className="min-h-[48px] rounded-btn border border-ink/20 px-5 text-body font-semibold text-ink hover:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" data-id="element-282">
              Back
            </button>
            <button type="button" onClick={() => setStep(3)} className="min-h-[48px] flex-1 rounded-btn bg-signal px-5 text-body font-semibold text-paper hover:bg-signal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" data-id="element-283">
              Continue
            </button>
          </div>
        </fieldset>}

      {step === 3 && <fieldset className="space-y-4" data-id="element-284">
          <legend className="text-h4 font-semibold text-ink" data-id="element-285">
            How can we reach you?
          </legend>
          <div data-id="element-286">
            <label htmlFor="qc-name" className="mb-1.5 block text-small font-medium text-ink" data-id="element-287">
              Name
            </label>
            <input id="qc-name" type="text" autoComplete="name" value={name} onChange={e => setName(e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'qc-name-err' : undefined} className={inputClass} data-id="element-288" />
            {errors.name && <p id="qc-name-err" className="mt-1 text-small text-signal" data-id="element-289">
                {errors.name}
              </p>}
          </div>
          <div data-id="element-290">
            <label htmlFor="qc-phone" className="mb-1.5 block text-small font-medium text-ink" data-id="element-291">
              Phone
            </label>
            <input id="qc-phone" type="tel" autoComplete="tel" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'qc-phone-err' : undefined} className={inputClass} data-id="element-292" />
            {errors.phone && <p id="qc-phone-err" className="mt-1 text-small text-signal" data-id="element-293">
                {errors.phone}
              </p>}
          </div>
          <div data-id="element-294">
            <label htmlFor="qc-email" className="mb-1.5 block text-small font-medium text-ink" data-id="element-295">
              Email <span className="text-slate/45" data-id="element-296">(optional)</span>
            </label>
            <input id="qc-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'qc-email-err' : undefined} className={inputClass} data-id="element-297" />
            {errors.email && <p id="qc-email-err" className="mt-1 text-small text-signal" data-id="element-298">
                {errors.email}
              </p>}
          </div>
          <div data-id="element-299">
            <label htmlFor="qc-time" className="mb-1.5 block text-small font-medium text-ink" data-id="element-300">
              Preferred contact time
            </label>
            <select id="qc-time" value={contactTime} onChange={e => setContactTime(e.target.value)} className={inputClass} data-id="element-301">
              <option value="" data-id="element-302">No preference</option>
              <option value="morning" data-id="element-303">Morning</option>
              <option value="afternoon" data-id="element-304">Afternoon</option>
              <option value="anytime" data-id="element-305">Anytime</option>
            </select>
          </div>

          {status === 'error' && <div role="alert" className="flex items-start gap-2 rounded-btn border border-signal/40 bg-signal/10 p-3 text-small text-ink" data-id="element-306">
              <AlertCircleIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-signal" strokeWidth={1.75} data-id="element-307" />
              Something went wrong sending your details. Please try again, or
              call {PHONE_DISPLAY}.
            </div>}

          <div className="flex gap-3 pt-1" data-id="element-308">
            <button type="button" onClick={() => setStep(2)} disabled={status === 'submitting'} className="min-h-[48px] rounded-btn border border-ink/20 px-5 text-body font-semibold text-ink hover:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal disabled:opacity-50" data-id="element-309">
              Back
            </button>
            <button type="submit" disabled={status === 'submitting'} className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-btn bg-signal px-5 text-body font-semibold text-paper hover:bg-signal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal disabled:opacity-70" data-id="element-310">
              {status === 'submitting' ? <>
                  <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" strokeWidth={2} data-id="element-311" />
                  Sending…
                </> : 'Request My Confidential Claim Review'}
            </button>
          </div>
          <p className="text-small text-slate/70" data-id="element-312">
            You do not need every document or detail before speaking with us.
            Your information is treated confidentially — see our{' '}
            <a href="/privacy/" className="text-forest underline underline-offset-2" data-id="element-313">
              privacy policy
            </a>
            .
          </p>
        </fieldset>}
    </form>;
}